import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TrendingUp, Activity, Target, BarChart3, Zap, Shield, BarChart2, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFILES = [
  {
    id: "apex",
    label: "Apex",
    tagline: "Aggressive Growth",
    Icon: Zap,
    accentColor: "#FF3D00",
    gradientFrom: "from-[#FF3D00]/20",
    borderColor: "border-[#FF3D00]",
    textColor: "text-[#FF3D00]",
    bgActive: "bg-[#FF3D00]",
    description: "High-risk, high-reward strategy targeting maximum returns with larger drawdowns.",
  },
  {
    id: "balanced",
    label: "Balanced",
    tagline: "Core Strategy",
    Icon: BarChart2,
    accentColor: "#007AFF",
    gradientFrom: "from-[#007AFF]/20",
    borderColor: "border-[#007AFF]",
    textColor: "text-[#007AFF]",
    bgActive: "bg-[#007AFF]",
    description: "Optimal blend of growth and risk management — our flagship strategy.",
  },
  {
    id: "shield",
    label: "Shield",
    tagline: "Capital Preservation",
    Icon: Shield,
    accentColor: "#00E676",
    gradientFrom: "from-[#00E676]/20",
    borderColor: "border-[#00E676]",
    textColor: "text-[#00E676]",
    bgActive: "bg-[#00E676]",
    description: "Conservative, low-drawdown approach focused on protecting capital.",
  },
];

// Fallback defaults keyed by profile id
const DEFAULTS = {
  apex: {
    stats: { win_rate: 68.2, max_drawdown: 24.5, sharpe_ratio: 1.42, expected_payoff: 112.80 },
    equityData: [
      { day: 1, value: 10000 }, { day: 2, value: 10310 }, { day: 3, value: 10180 }, { day: 4, value: 10620 },
      { day: 5, value: 11050 }, { day: 6, value: 10780 }, { day: 7, value: 11400 }, { day: 8, value: 11100 },
      { day: 9, value: 11900 }, { day: 10, value: 12500 }, { day: 11, value: 12100 }, { day: 12, value: 12800 },
      { day: 13, value: 13400 }, { day: 14, value: 13000 }, { day: 15, value: 13900 }, { day: 16, value: 14500 },
      { day: 17, value: 14100 }, { day: 18, value: 15000 }, { day: 19, value: 15800 }, { day: 20, value: 15400 },
    ],
    monthlyReturns: [
      { month: "Jan", return: "5.2" }, { month: "Feb", return: "-3.1" }, { month: "Mar", return: "7.8" },
      { month: "Apr", return: "4.2" }, { month: "May", return: "9.1" }, { month: "Jun", return: "-2.4" },
      { month: "Jul", return: "6.5" }, { month: "Aug", return: "-1.8" }, { month: "Sep", return: "8.3" },
      { month: "Oct", return: "5.7" }, { month: "Nov", return: "-4.2" }, { month: "Dec", return: "11.3" },
    ],
  },
  balanced: {
    stats: { win_rate: 72.4, max_drawdown: 12.8, sharpe_ratio: 1.85, expected_payoff: 45.20 },
    equityData: [
      { day: 1, value: 10000 }, { day: 2, value: 10120 }, { day: 3, value: 10050 }, { day: 4, value: 10250 },
      { day: 5, value: 10400 }, { day: 6, value: 10320 }, { day: 7, value: 10600 }, { day: 8, value: 10500 },
      { day: 9, value: 10850 }, { day: 10, value: 10900 }, { day: 11, value: 10800 }, { day: 12, value: 11100 },
      { day: 13, value: 11350 }, { day: 14, value: 11200 }, { day: 15, value: 11500 }, { day: 16, value: 11700 },
      { day: 17, value: 11650 }, { day: 18, value: 11900 }, { day: 19, value: 12200 }, { day: 20, value: 12100 },
    ],
    monthlyReturns: [
      { month: "Jan", return: "2.5" }, { month: "Feb", return: "-1.2" }, { month: "Mar", return: "3.4" },
      { month: "Apr", return: "1.8" }, { month: "May", return: "4.1" }, { month: "Jun", return: "2.2" },
      { month: "Jul", return: "-0.5" }, { month: "Aug", return: "1.5" }, { month: "Sep", return: "2.9" },
      { month: "Oct", return: "3.1" }, { month: "Nov", return: "-1.0" }, { month: "Dec", return: "4.5" },
    ],
  },
  shield: {
    stats: { win_rate: 81.2, max_drawdown: 5.4, sharpe_ratio: 2.31, expected_payoff: 18.60 },
    equityData: [
      { day: 1, value: 10000 }, { day: 2, value: 10065 }, { day: 3, value: 10045 }, { day: 4, value: 10110 },
      { day: 5, value: 10180 }, { day: 6, value: 10160 }, { day: 7, value: 10240 }, { day: 8, value: 10220 },
      { day: 9, value: 10300 }, { day: 10, value: 10370 }, { day: 11, value: 10350 }, { day: 12, value: 10430 },
      { day: 13, value: 10510 }, { day: 14, value: 10490 }, { day: 15, value: 10570 }, { day: 16, value: 10650 },
      { day: 17, value: 10630 }, { day: 18, value: 10720 }, { day: 19, value: 10800 }, { day: 20, value: 10850 },
    ],
    monthlyReturns: [
      { month: "Jan", return: "1.1" }, { month: "Feb", return: "0.8" }, { month: "Mar", return: "1.4" },
      { month: "Apr", return: "0.6" }, { month: "May", return: "1.2" }, { month: "Jun", return: "0.9" },
      { month: "Jul", return: "1.0" }, { month: "Aug", return: "0.7" }, { month: "Sep", return: "1.3" },
      { month: "Oct", return: "1.1" }, { month: "Nov", return: "0.5" }, { month: "Dec", return: "1.5" },
    ],
  },
};

const OWNER_EMAIL = "kairosiscapital@gmail.com";

export default function Performance() {
  const [activeProfile, setActiveProfile] = useState("balanced");
  const [profileData, setProfileData] = useState(DEFAULTS);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadStatus, setReloadStatus] = useState(null); // null | "ok" | "error"

  // Detect owner from localStorage
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isOwner = storedUser?.email === OWNER_EMAIL;

  const fetchProfile = useCallback(async (pid) => {
    const res = await axios.get(`${API}/stats`, { params: { profile: pid } });
    if (res.data && res.data.stats) {
      setProfileData(prev => ({ ...prev, [pid]: res.data }));
    }
  }, []);

  const reloadAll = useCallback(async () => {
    setRefreshing(true);
    setReloadStatus(null);
    const results = await Promise.allSettled(PROFILES.map(p => fetchProfile(p.id)));
    const anyOk = results.some(r => r.status === "fulfilled");
    setRefreshing(false);
    setReloadStatus(anyOk ? "ok" : "error");
    setTimeout(() => setReloadStatus(null), 3000);
  }, [fetchProfile]);

  useEffect(() => {
    // Silently prefetch all profiles on every page visit
    PROFILES.forEach(p => fetchProfile(p.id).catch(() => { }));
  }, [fetchProfile]);

  const data = profileData[activeProfile];
  const profile = PROFILES.find(p => p.id === activeProfile);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start justify-between mb-8 sm:mb-10 gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">Performance Stats</h1>
              <p className="text-sm sm:text-base text-[#A1A1AA]">Live metrics for each strategy profile</p>
            </div>

            {/* Owner-only Reload CSV button */}
            {isOwner && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={reloadAll}
                  disabled={refreshing}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${reloadStatus === "ok"
                      ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10"
                      : reloadStatus === "error"
                        ? "border-[#FF3D00] text-[#FF3D00] bg-[#FF3D00]/10"
                        : "border-[#27272A] text-[#A1A1AA] hover:border-[#007AFF] hover:text-white"
                    } disabled:opacity-50`}
                  title="Reload all profile data from CSV"
                >
                  {reloadStatus === "ok" ? (
                    <><CheckCircle className="w-4 h-4" /> Updated!</>
                  ) : reloadStatus === "error" ? (
                    <><AlertCircle className="w-4 h-4" /> Backend unreachable</>
                  ) : (
                    <><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Reload CSV</>
                  )}
                </button>
                {reloadStatus === "error" && (
                  <p className="text-[10px] text-[#FF3D00]/70">Make sure uvicorn is running</p>
                )}
              </div>
            )}
          </div>

          {/* Profile Selector */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {PROFILES.map(({ id, label, tagline, Icon, accentColor, borderColor, textColor, bgActive, description }) => {
              const isActive = activeProfile === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveProfile(id)}
                  className={`relative text-left rounded-2xl border p-5 transition-all duration-200 focus:outline-none
                    ${isActive
                      ? `${borderColor} bg-[#111111] shadow-lg`
                      : "border-[#27272A] bg-[#0D0D0D] hover:border-[#3F3F46]"
                    }`}
                >
                  {isActive && (
                    <span
                      className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      ACTIVE
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${accentColor}22` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: accentColor }} />
                    </div>
                    <div>
                      <p className="font-bold text-white text-base">{label}</p>
                      <p className="text-xs" style={{ color: accentColor }}>{tagline}</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#71717A] leading-relaxed">{description}</p>
                </button>
              );
            })}
          </div>

          {/* Stats Grid */}
          {data && data.stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Win Rate", value: `${data.stats.win_rate}%`, Icon: TrendingUp, color: profile.accentColor },
                { label: "Max Drawdown", value: `${data.stats.max_drawdown}%`, Icon: BarChart3, color: "#A1A1AA" },
                { label: "Sharpe Ratio", value: data.stats.sharpe_ratio, Icon: Activity, color: profile.accentColor },
                { label: "Expected Payoff", value: `$${data.stats.expected_payoff}`, Icon: Target, color: "#FFC107" },
              ].map(({ label, value, Icon, color }) => (
                <Card key={label} className="bg-[#111111] border-[#27272A]">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <Icon className="w-4 h-4" style={{ color }} />
                      <p className="text-[10px] sm:text-xs text-[#A1A1AA] font-medium">{label}</p>
                    </div>
                    <p className="text-xl sm:text-3xl font-bold font-mono" style={{ color }}>{value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Equity Curve */}
          {data && data.equityData && (
            <Card className="bg-[#111111] border-[#27272A] mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: profile.accentColor }} />
                  {profile.label} — Equity Curve
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.equityData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                      <XAxis dataKey="day" stroke="#3F3F46" tick={{ fill: "#71717A", fontSize: 11 }} />
                      <YAxis stroke="#3F3F46" tick={{ fill: "#71717A", fontSize: 11 }} domain={["auto", "auto"]} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#18181B", border: "1px solid #27272A", borderRadius: "10px" }}
                        labelStyle={{ color: "#A1A1AA" }}
                        formatter={(v) => [`$${v.toLocaleString()}`, "Balance"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={profile.accentColor}
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5, fill: profile.accentColor }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Monthly Returns */}
          {data && data.monthlyReturns && (
            <Card className="bg-[#111111] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-base">Monthly Returns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {data.monthlyReturns.map((item) => {
                    const pos = parseFloat(item.return) >= 0;
                    return (
                      <div
                        key={item.month}
                        className={`p-4 rounded-xl text-center border transition-all ${pos
                            ? "bg-[#00E676]/10 border-[#00E676]/30"
                            : "bg-[#FF3D00]/10 border-[#FF3D00]/30"
                          }`}
                      >
                        <p className="text-xs text-[#71717A] mb-1 font-medium">{item.month}</p>
                        <p className={`text-base font-bold font-mono ${pos ? "text-[#00E676]" : "text-[#FF3D00]"}`}>
                          {pos ? "+" : ""}{item.return}%
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
